

















































































































































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import base64toUInt8Array, { openPdfInNewTab } from "@/utils/fileConvert";
import DateInput from "@/components/DateInput.vue";
import CheckBox from "@/components/CheckBox.vue";
import Tabs from "@/components/Tabs.vue";
import api from "../services/api";
import {
  ReportHeadOption,
  RunReportParams,
  DefaultRunReportParams,
  ReportResultRow,
  ReportDimensionFilter,
} from "../models/ReportRun";


interface SelectableDimRow {
  id: number;
  dimcode: string;
  national: string;
  selected: boolean;
}

interface DimGroup {
  label: string;
  types: number[];
}

interface Data {
  params: RunReportParams;
  accountCheck: boolean;
  reportHeadOptions: ReportHeadOption[];
  dimGroups: DimGroup[];
  dimRows: { [type: number]: SelectableDimRow[] };
  dimYear: number;
  rows: ReportResultRow[];
  runId: number;
  running: boolean;
  exporting: boolean;
  columns: any[];
  dimColumns: any[];
}
interface Methods {
  loadReportHeads(): Promise<void>;
  loadDimensions(): Promise<void>;
  dimTypeLabel(type: number): string;
  allSelected(type: number): boolean;
  selectAll(type: number, all: boolean): void;
  toggleDim(row: SelectableDimRow): void;
  collectDimensionFilters(): ReportDimensionFilter[];
  run(): Promise<void>;
  loadRows(): Promise<void>;
  printPdf(): Promise<void>;
  exportExcel(): Promise<void>;
  reset(): void;
  formatValue(v: number | null): string;
  rowStyle(row: ReportResultRow): object;
}

// K2D tab-feliratok (ST_DIM / MSG_DIM_4-6 / MSG_DIM_7-9); type 2..10 = I..IX. dimenzió
const RomanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"];

export default Vue.extend<Data, Methods, {}, {}>({
  components: { DateInput, CheckBox, Tabs },

  data(): Data {
    const dimRows: { [type: number]: SelectableDimRow[] } = {};
    for (let type = 2; type <= 10; type++) dimRows[type] = [];
    return {
      params: { ...DefaultRunReportParams, mode: "P" },
      accountCheck: false,
      reportHeadOptions: [],
      dimGroups: [
        { label: "I–III. dimenzió", types: [2, 3, 4] },
        { label: "IV–VI. dimenzió", types: [5, 6, 7] },
        { label: "VII–IX. dimenzió", types: [8, 9, 10] },
      ],
      dimRows,
      dimYear: 0,
      rows: [],
      runId: 0,
      running: false,
      exporting: false,
      columns: [
        { field: "orderNo", title: "Sorsz.", width: "70px", cell: "orderNoTemplate" },
        { field: "code", title: "Kód", width: "140px", cell: "codeTemplate" },
        { field: "name", title: "Megnevezés", cell: "nameTemplate" },
        { field: "previousYear", title: "Előző év", width: "150px", cell: "previousYearTemplate" },
        { field: "previousYearMod", title: "Előző év módosításai", width: "160px", cell: "previousYearModTemplate" },
        { field: "currentYear", title: "Tárgyév", width: "150px", cell: "currentYearTemplate" },
      ],
      dimColumns: [
        { field: "selected", title: " ", width: "60px", cell: "dimSelTemplate" },
        { field: "dimcode", title: "Dimenzió kód", width: "120px" },
        { field: "national", title: "Megnevezés" },
      ],
    };
  },

  async beforeMount() {
    await Promise.all([this.loadReportHeads(), this.loadDimensions()]);
  },

  methods: {
    async loadReportHeads() {
      try {
        this.reportHeadOptions = await api.GetReportHeads(4);
        if (this.reportHeadOptions.length === 0) {
          eventBus.$emit("error", "Nincs főkönyvi riport típusú riport-definíció!");
          return;
        }
        this.params.reportHeadId = this.reportHeadOptions[0].id;
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A riport-definíciók betöltése nem sikerült!");
      }
    },

    async loadDimensions() {
      const year = dayjs(this.params.fromDate).year();
      if (!year || year === this.dimYear) return;
      try {
        const codes = await api.GetDimensionCodesByYear(year);
        this.dimYear = year;
        const previous = this.dimRows;
        const dimRows: { [type: number]: SelectableDimRow[] } = {};
        for (let type = 2; type <= 10; type++) {
          const selectedCodes = (previous[type] || []).filter(r => r.selected).map(r => r.dimcode);
          dimRows[type] = codes
            .filter((c: any) => c.type === type)
            .map((c: any) => ({
              id: c.id,
              dimcode: c.dimcode,
              national: c.national,
              selected: selectedCodes.includes(c.dimcode),
            }));
        }
        this.dimRows = dimRows;
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A dimenzió-listák betöltése nem sikerült!");
      }
    },

    dimTypeLabel(type: number): string {
      return `${RomanNumerals[type - 2]}. dimenzió`;
    },

    allSelected(type: number): boolean {
      const rows = this.dimRows[type] || [];
      return rows.length > 0 && rows.every(r => r.selected);
    },

    selectAll(type: number, all: boolean) {
      (this.dimRows[type] || []).forEach(r => (r.selected = all));
    },

    toggleDim(row: SelectableDimRow) {
      row.selected = !row.selected;
    },

    collectDimensionFilters(): ReportDimensionFilter[] {
      const filters: ReportDimensionFilter[] = [];
      for (let type = 2; type <= 10; type++) {
        const dimCodes = (this.dimRows[type] || []).filter(r => r.selected).map(r => r.dimcode);
        if (dimCodes.length > 0) filters.push({ type, dimCodes });
      }
      return filters;
    },

    async run() {
      if (!this.params.reportHeadId || this.params.reportHeadId <= 1) {
        eventBus.$emit("error", "Válasszon riportot!");
        return;
      }
      if (!this.params.fromDate || !this.params.toDate) {
        eventBus.$emit("error", "Adja meg az intervallumot!");
        return;
      }

      this.running = true;
      eventBus.$emit("busy-message", true);

      if (this.accountCheck) {
        try {
          const checkRows = await api.GetReportAccountCheck({
            reportHeadId: this.params.reportHeadId,
            mode: "P",
            fromDate: this.params.fromDate,
            toDate: this.params.toDate,
          });
          if (checkRows.length > 0) {
            eventBus.$emit(
              "error",
              `Fksz. ellenőrzés: ${checkRows.length} könyvelt tétel nem esik bele a riport fksz-maszkjaiba!`
            );
          }
        } catch (error) {
          console.error(error);
          eventBus.$emit("error", "Az fksz. ellenőrzés nem sikerült!");
          this.running = false;
          eventBus.$emit("busy-message", false);
          return;
        }
      }

      this.params.mode = "P";
      this.params.selectedRowIds = [];
      this.params.dimensionFilters = this.collectDimensionFilters();

      try {
        const result = await api.RunReport(this.params);
        if (!result.success) {
          eventBus.$emit("error", result.message || "A riport futtatása nem sikerült!");
          return;
        }
        this.runId = result.runId;
        await this.loadRows();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A riport futtatása nem sikerült!");
      } finally {
        this.running = false;
        eventBus.$emit("busy-message", false);
      }
    },

    async loadRows() {
      this.rows = (await api.GetReportRows(this.runId, "P", 1)).map(r => ({
        ...r,
        previousYear: r.previousYearId != null && r.previousYearId > 0 && r.previousYear == null ? 0 : r.previousYear,
        previousYearMod: r.previousYearModId != null && r.previousYearModId > 0 && r.previousYearMod == null ? 0 : r.previousYearMod,
        currentYear: r.currentYearId > 0 && r.currentYear == null ? 0 : r.currentYear,
      }));
    },

    async printPdf() {
      this.exporting = true;
      eventBus.$emit("busy-message", true);
      try {
        const base64 = await api.GetReportPdf({
          runId: this.runId,
          mode: "P",
          inThousands: this.params.inThousands,
          filler: this.params.filler,
          issueDate: dayjs().format("YYYY-MM-DD"),
          zeroItemVisible: false,
          lang: 1,
        });
        if (!base64) {
          eventBus.$emit("error", "A riport nyomtatása nem sikerült!");
          return;
        }
        openPdfInNewTab(base64toUInt8Array(base64));
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "A riport nyomtatása nem sikerült!");
      } finally {
        this.exporting = false;
        eventBus.$emit("busy-message", false);
      }
    },

    async exportExcel() {
      this.exporting = true;
      eventBus.$emit("busy-message", true);
      try {
        const file = await api.GetReportExcel(this.runId, "P", 1);
        if (!file) {
          eventBus.$emit("error", "Az Excel export nem sikerült!");
          return;
        }
        const downloadLink = document.createElement("a");
        downloadLink.href = `data:application/vnd.openxmlformats;base64,${file}`;
        downloadLink.download = `Fokonyvi_riport_${this.runId}.xlsx`;
        downloadLink.click();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", "Az Excel export nem sikerült!");
      } finally {
        this.exporting = false;
        eventBus.$emit("busy-message", false);
      }
    },

    reset() {
      const reportHeadId = this.reportHeadOptions.length > 0 ? this.reportHeadOptions[0].id : 0;
      this.params = { ...DefaultRunReportParams, mode: "P", reportHeadId };
      this.accountCheck = false;
      for (let type = 2; type <= 10; type++) this.selectAll(type, false);
    },

    formatValue(v: number | null): string {
      if (v === null || v === undefined) return "";
      return v.toLocaleString("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    rowStyle(row: ReportResultRow): object {
      return {
        fontWeight: row.isBold ? 700 : 400,
        fontStyle: row.isItalic ? "italic" : "normal",
      };
    },
  },

  watch: {
    "params.fromDate"() {
      this.loadDimensions();
    },
  },
});
